// src/pages/RunDetail.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Tabs,
  Tab,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { getRun, getRunUsage } from "../utils/api";
import { labelFor } from "../utils/labelUtils";
import NewRunDialog from "../components/NewRunDialog";

const statusColor = (s = "") => {
  const v = s.toLowerCase();
  if (v === "succeeded" || v === "success") return "success";
  if (v === "running" || v === "queued") return "info";
  if (v === "failed" || v === "error") return "error";
  return "default";
};

const errorHints = {
  invalid_model_problem:
    "Try rewording your question as a prediction or estimation task.",
  unclear_outcome:
    "Specify the outcome you want to predict, e.g., sales or churn.",
  unclear_prediction_level:
    "Clarify what level should be predicted — e.g., per user, product, or campaign.",
  dictionary_format_error:
    "Double-check your YAML or TXT dictionary formatting in the dictionary editor.",
  dictionary_missing:
    "Please upload or link a data dictionary before starting a run.",
  simulation_error:
    "Simulation failed — simplify relationships or verify foreign keys.",
  timeout_error:
    "The run took too long. Try simplifying your question or data.",
  bigquery_dataset_missing:
    "Data storage not found. Check your project and dataset settings.",
};

export default function RunDetail() {
  const { id } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState(null);
  const [err, setErr] = useState("");
  const [usage, setUsage] = useState(null);
  const [tab, setTab] = useState(0);

  const [openRerun, setOpenRerun] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getRun(id);
        if (mounted) {
          setRun(data);
          setErr("");
        }
      } catch (e) {
        setErr(e?.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    // also fetch usage (to get license tier)
    (async () => {
      try {
        const data = await getRunUsage();
        if (mounted) setUsage(data);
      } catch (e) {
        console.error("getRunUsage failed", e);
      }
    })();

    return () => (mounted = false);
  }, [id]);

  const params = useMemo(() => run?.params ?? {}, [run]);

  // Friendly fallbacks
  const runName = params.run_name || "";
  const datasetProject = params.dataset_project || params.project || "—";
  const dataDictLabel =
    params.input_file_name ||
    params.dict_name ||
    params.dict_id ||
    "—";

  // Determine rerun capability based on tier and status
  const tier = usage?.tier || "free";
  const status = (run?.status || "").toLowerCase();

  const rerunDisabled =
    (tier === "free" && status !== "failed") ||
    (tier === "pro" && !["failed", "canceled"].includes(status)) ||
    status === "running" ||
    status === "queued";

  const rerunTooltip =
    tier === "free"
      ? "Free users can only rerun failed runs."
      : tier === "pro"
      ? "Pro users can rerun failed or canceled runs."
      : "Enterprise users can rerun any run.";

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" height={40} width={320} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={24} width={260} />
        <Skeleton variant="rounded" height={140} sx={{ mt: 3 }} />
      </Box>
    );
  }

  if (err) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700} mb={1}>
          Analysis
        </Typography>
        <Typography color="error">{err}</Typography>
      </Box>
    );
  }

  if (!run) return null;

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formatDate = (ts) => {
    if (!ts) return "—";
    try {
      if (ts.seconds)
        return new Date(ts.seconds * 1000).toLocaleString("en-US", {
          timeZone: userTimeZone,
        });
      return new Date(ts).toLocaleString("en-US", { timeZone: userTimeZone });
    } catch {
      return String(ts);
    }
  };

  const created = formatDate(run.created_at);
  const started = formatDate(run.started_at);
  const finished = formatDate(run.finished_at);

  return (
    <>
      <Stack spacing={3}>
        {/* Title */}
        <Box>
          <Typography variant="h4" fontWeight={800} gutterBottom sx={{ wordBreak: 'break-all' }}>
            {runName || id}
          </Typography>
          {runName && (
            <Typography variant="body2" color="text.secondary">
              Analysis ID: {id}
            </Typography>
          )}
        </Box>

        {/* Status + primary actions */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="subtitle1">Status:</Typography>
              <Chip
                label={run.status ?? "unknown"}
                color={statusColor(run.status)}
                size="small"
                sx={{ fontWeight: 700, textTransform: "capitalize" }}
              />
              {run.primary_metric && run.metric_value != null && (
                <Chip
                  label={`${run.primary_metric} ${Number(run.metric_value).toFixed(3)}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {/* v2 runs originate in a chat session — link back so the auditor
                  can see the conversation that built this run. */}
              {run._v2_native && run.source_session_id && (
                <Button
                  variant="outlined"
                  startIcon={<ChatBubbleOutlineIcon />}
                  onClick={() => nav(`/analyze/${run.source_session_id}`)}
                >
                  Open chat
                </Button>
              )}

              {/* v1-specific deep links. v2 doesn't produce a notebook in the
                  same shape, so these are disabled for v2 runs to avoid 404s. */}
              {!run._v2_native && (
                <>
                  <Button
                    variant="contained"
                    onClick={() => nav(`/runs/${id}/notebook`)}
                    disabled={!(run.has_result && run.status === "succeeded")}
                  >
                    View decisions
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => nav(`/runs/${id}/data`)}
                  >
                    Detailed data
                  </Button>
                </>
              )}

              {!run._v2_native && (
                <Tooltip title={rerunTooltip}>
                  <span>
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={rerunDisabled}
                      onClick={() => setOpenRerun(true)}
                    >
                      Rerun
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* Error details (shown above tabs because failures take precedence) */}
        {run.status === "failed" && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderColor: "error.main",
              bgcolor: "error.lighter",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              color="error.main"
              gutterBottom
            >
              Analysis failed
            </Typography>

            {run.error_type && (
              <Chip
                label={run.error_type.replaceAll("_", " ").toUpperCase()}
                color="error"
                size="small"
                sx={{ mb: 1 }}
              />
            )}

            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {run.error_friendly || run.error_message || run.error || "An unknown error occurred."}
            </Typography>

            {run.error_trace && (
              <Box
                component="pre"
                sx={{
                  mt: 1.5, p: 1.5, borderRadius: 1,
                  bgcolor: "action.hover", fontSize: 11, fontFamily: "monospace",
                  overflow: "auto", maxHeight: 320,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}
              >
                {run.error_trace}
              </Box>
            )}

            {run.error_type && errorHints[run.error_type] && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                💡 {errorHints[run.error_type]}
              </Typography>
            )}
          </Paper>
        )}

        {/* Tabs: Overview (params + summary), Pipeline (v2 events timeline) */}
        <Box>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36, borderBottom: 1, borderColor: "divider" }}>
            <Tab label="Overview" sx={{ textTransform: "none", minHeight: 36 }} />
            <Tab
              label="Pipeline"
              disabled={!run._v2_native}
              sx={{ textTransform: "none", minHeight: 36 }}
            />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {/* v2 summary card — top drivers, label distribution, dataset */}
              {run._v2_native && (
                <V2SummaryCard run={run} />
              )}

              {/* Key parameters */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Parameters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <LabelValue label="Analysis name" value={runName || "—"} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LabelValue label="Data Dictionary" value={dataDictLabel} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LabelValue
                      label={labelFor("project", params.platform)}
                      value={datasetProject}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LabelValue
                      label={labelFor("dataset", params.platform)}
                      value={params.dataset || "—"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LabelValue label="SQL Platform" value={params.platform || "—"} />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <LabelValue
                      label="Your business question"
                      value={params.problem_statement || "—"}
                      multiline
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Timestamps */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Timestamps
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <LabelValue label="Created" value={created} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <LabelValue label="Started" value={started} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <LabelValue label="Finished" value={finished} />
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          )}

          {tab === 1 && (
            <Box sx={{ mt: 2 }}>
              <PipelineTimeline events={run.events || []} />
            </Box>
          )}
        </Box>
      </Stack>

      {/* Rerun Dialog */}
      {openRerun && (
        <NewRunDialog
          open={openRerun}
          onClose={() => setOpenRerun(false)}
          onSuccess={async () => {
            const refreshed = await getRun(id);
            setRun(refreshed);
            setOpenRerun(false);
          }}
          initialValues={{
            run_name: params.run_name || "",
            problem_statement: params.problem_statement || "",
            platform: params.platform || "",
            project: params.project || "",
            dataset: params.dataset || "",
            dict_id: params.dict_id || "",
            dict_name: params.dict_name || "",
            input_file_name: params.input_file_name || "",
          }}
          runId={id}
          mode="rerun"
        />
      )}
    </>
  );
}

function LabelValue({ label, value, multiline = false }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Divider sx={{ my: 0.5 }} />
      <Typography
        sx={{
          whiteSpace: multiline ? "pre-wrap" : "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}


// ─── v2 summary card (Overview tab top) ──────────────────────────────────────
// Compact card that surfaces the run's "what it concluded" view: problem
// statement, dataset, label distribution, top drivers, entity keys. This is
// the v2 equivalent of v1's "decision_output" preview.

function V2SummaryCard({ run }) {
  const params = run.params || {};
  const drivers = run.top_drivers || [];
  const ls = run.label_stats || {};
  const totalRows = ls.total != null ? ls.total : null;
  const winRate = ls.win_rate_pct != null ? ls.win_rate_pct : null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Run summary
      </Typography>
      {params.problem_statement && (
        <Typography variant="body2" sx={{ fontSize: 14, fontStyle: "italic", mb: 1.5 }}>
          "{params.problem_statement}"
        </Typography>
      )}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {run.bigquery_dataset && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{
              display: "block", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
            }}>
              Dataset
            </Typography>
            <Box component="code" sx={{ fontFamily: "monospace", fontSize: 13 }}>
              {run.bigquery_dataset}
            </Box>
          </Box>
        )}
        {totalRows != null && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{
              display: "block", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
            }}>
              Label distribution
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {totalRows.toLocaleString()} rows
              {winRate != null && ` · ${winRate}% positive`}
              {ls.won != null && ls.lost != null && ` (${ls.won} won / ${ls.lost} lost)`}
            </Typography>
          </Box>
        )}
        {run.entity_keys && run.entity_keys.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{
              display: "block", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
            }}>
              Entity key
            </Typography>
            <Box component="code" sx={{ fontFamily: "monospace", fontSize: 13 }}>
              {run.entity_keys.join(", ")}
            </Box>
          </Box>
        )}
      </Stack>
      {drivers.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{
            display: "block", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5,
          }}>
            Top drivers ({drivers.length})
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {drivers.map((d, i) => (
              <Chip key={i} label={d} size="small" variant="outlined"
                sx={{ fontSize: 11, height: 22, fontFamily: "monospace" }} />
            ))}
          </Box>
        </Box>
      )}
      {run.drivers_description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontSize: 13 }}>
          {run.drivers_description}
        </Typography>
      )}
    </Paper>
  );
}


// ─── Pipeline timeline (Overview tab → Pipeline tab) ─────────────────────────
// Renders the v2 events array as a chronological timeline. Each event is a row
// with timestamp + type label + a payload-derived headline + expandable full
// payload JSON. The headline formatter encodes domain knowledge of each event
// type so an auditor can scan without expanding everything.

const EVENT_ICON = {
  pipeline_started: PlayArrowIcon,
  source_copied: PlayArrowIcon,
  intent_classified: AutoFixHighIcon,
  columns_classified: AutoFixHighIcon,
  pred_grid_built: AutoFixHighIcon,
  label_built: AutoFixHighIcon,
  feature_plan_done: AutoFixHighIcon,
  features_materialized: AutoFixHighIcon,
  features_gated: AutoFixHighIcon,
  train_models: AutoFixHighIcon,
  eval: AutoFixHighIcon,
  iteration_turn: AutoFixHighIcon,
  iteration_complete: CheckCircleOutlineIcon,
  pipeline_complete: CheckCircleOutlineIcon,
  pipeline_failed: ErrorOutlineIcon,
};

function _eventHeadline(type, payload) {
  if (!payload || typeof payload !== "object") return "";
  switch (type) {
    case "source_copied":
      return `${(payload.source_tables || []).join(", ")} → ${payload.scratch_dataset}`;
    case "intent_classified":
      return `${payload.problem_type || "?"} of ${payload.entity_grain || "?"} (${payload.entity_type || "?"}, ${payload.temporal_structure || "?"})  ·  target: ${payload.target_column || "?"}`;
    case "columns_classified":
      return `${payload.n_columns ?? "?"} columns · ${payload.n_groups ?? "?"} groups · ${payload.n_measures ?? "?"} measures`;
    case "pred_grid_built":
    case "label_built":
      return `${(payload.row_count ?? 0).toLocaleString()} rows`;
    case "feature_plan_done":
      return `${payload.n_features ?? "?"} candidate features`;
    case "features_materialized":
      return `${payload.n_surviving ?? "?"} survived materialization`;
    case "features_gated": {
      const kept = payload.n_kept ?? "?";
      const dropped = payload.n_dropped ?? "?";
      const top = (payload.top_features || []).slice(0, 3).join(", ");
      return `kept ${kept}, dropped ${dropped}${top ? ` · top: ${top}` : ""}`;
    }
    case "iteration_turn": {
      const decision = payload.decision || "?";
      const before = payload.before_metric;
      const after = payload.after_metric;
      const delta = (after != null && before != null) ? (after - before) : null;
      const deltaStr = delta != null
        ? ` (${delta >= 0 ? "+" : ""}${delta.toFixed(4)})`
        : "";
      const skill = payload.skill ? ` · ${payload.skill}` : "";
      return `#${payload.n ?? "?"} · ${decision}${skill}${deltaStr}`;
    }
    case "iteration_complete":
      return `${payload.stop_reason || "?"} · ${payload.total_iterations ?? "?"} turns · final metric: ${payload.final_metric != null ? Number(payload.final_metric).toFixed(4) : "?"}`;
    case "pipeline_failed":
      return payload.error || "failed";
    default: {
      // Generic: take the most informative key
      const keys = Object.keys(payload).filter((k) => k !== "run_id");
      if (keys.length === 0) return "";
      const first = keys[0];
      const v = payload[first];
      return `${first}: ${typeof v === "object" ? JSON.stringify(v).slice(0, 80) : String(v).slice(0, 80)}`;
    }
  }
}

function _fmtTs(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return ts; }
}

function PipelineEventRow({ ev, prev }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const type = ev.type || "event";
  const payload = ev.payload || {};
  const headline = _eventHeadline(type, payload);
  const Icon = EVENT_ICON[type] || PlayArrowIcon;
  const isFailure = type === "pipeline_failed";
  const isComplete = type === "pipeline_complete" || type === "iteration_complete";
  const iconColor = isFailure
    ? theme.palette.error.main
    : isComplete
    ? theme.palette.success.main
    : theme.palette.primary.main;

  // Duration since previous event
  let elapsed = "";
  if (prev && ev.ts && prev.ts) {
    const diffMs = new Date(ev.ts).getTime() - new Date(prev.ts).getTime();
    if (!isNaN(diffMs) && diffMs >= 0) {
      if (diffMs < 1000) elapsed = `+${diffMs}ms`;
      else if (diffMs < 60_000) elapsed = `+${(diffMs / 1000).toFixed(1)}s`;
      else elapsed = `+${(diffMs / 60_000).toFixed(1)}m`;
    }
  }

  return (
    <Box
      onClick={() => setOpen((v) => !v)}
      sx={{
        display: "flex", alignItems: "flex-start", gap: 1.5,
        py: 1, px: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: "pointer",
        "&:hover": { bgcolor: theme.palette.action.hover },
      }}
    >
      <Icon sx={{ fontSize: 18, color: iconColor, mt: 0.25, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Box sx={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>
            {type}
          </Box>
          <Typography variant="caption" color="text.disabled">{_fmtTs(ev.ts)}</Typography>
          {elapsed && (
            <Typography variant="caption" color="text.disabled">{elapsed}</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="primary" sx={{ userSelect: "none" }}>
            {open ? "▴" : "▾"}
          </Typography>
        </Box>
        {headline && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mt: 0.25 }}>
            {headline}
          </Typography>
        )}
        {open && (
          <Box
            component="pre"
            sx={{
              mt: 1, p: 1, borderRadius: 1,
              bgcolor: "action.hover",
              fontSize: 11, fontFamily: "monospace",
              overflow: "auto", maxHeight: 280,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {JSON.stringify(payload, null, 2)}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function PipelineTimeline({ events }) {
  if (!events || events.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No pipeline events recorded for this run.
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{
        px: 1.5, py: 1.25,
        borderBottom: 1, borderColor: "divider",
        display: "flex", alignItems: "baseline", gap: 1,
      }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, fontSize: 11 }}>
          Pipeline events
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {events.length} event{events.length === 1 ? "" : "s"} · click to expand payload
        </Typography>
      </Box>
      {events.map((ev, i) => (
        <PipelineEventRow key={i} ev={ev} prev={i > 0 ? events[i - 1] : null} />
      ))}
    </Paper>
  );
}